// Reading speed used to size the display time, words per minute. Deliberately
// pessimistic against the ~200-250 wpm range for prose on screen, because a toast is
// read while looking at something else.
const READING_WPM = 160;

// A toast never sits for less than this, so a two-word confirmation still registers,
// nor longer than this, so a long one does not camp in the corner. Past the cap the
// reader dismisses it by clicking, which is why that is supported.
const MIN_MS = 3000;
const MAX_MS = 30000;

/**
 * How long a message needs to be on screen to be readable.
 *
 * The pipeline's findings are sentences, not labels: a run can report rejected
 * landmarks, a constricted arch and the Refine caveat at once, which came to 130
 * words. At the fixed 12s those callers asked for, that was 12 seconds to read 39
 * seconds of text, so the end of the message was never seen.
 */
function readingTimeMs(message) {
    const words = String(message).trim().split(/\s+/).filter(Boolean).length;
    return Math.min(MAX_MS, Math.max(MIN_MS, (words / READING_WPM) * 60000));
}

/**
 * Show a transient message.
 *
 * @param {string} message plain text; markup is escaped, not rendered
 * @param {string} icon Font Awesome name, without the `fa-` prefix
 * @param {number} [duration] milliseconds. Omit to size it from the message length,
 *   which is what long findings need. An explicit value is honoured as a floor, so a
 *   caller asking for 12s on a 100-word message still gets long enough to read it.
 */
function showToast(message, icon = "check", duration) {
    const toast = document.createElement("div");
    toast.className = "toast";

    // The icon is ours; the message is not. Filenames and server text reach here --
    // `Labels and tags saved to ${jsonFileName}` among them -- so the message goes in
    // as a text node rather than through innerHTML, where a name like
    // `<img src=x onerror=...>.json` would execute.
    const glyph = document.createElement("i");
    glyph.className = `fas fa-${icon}`;
    toast.appendChild(glyph);
    toast.appendChild(document.createTextNode(` ${message}`));

    const ms = Math.max(duration || 0, readingTimeMs(message));
    toast.style.animation = `fadeInOut ${ms / 1000}s ease-in-out`;

    // Dismissable, because a long finding holds the corner for a while and the reader
    // may be done with it sooner.
    toast.title = "Click to dismiss";
    const remove = () => toast.remove();
    toast.addEventListener("click", remove);

    document.body.appendChild(toast);
    setTimeout(remove, ms);
}

export { showToast, readingTimeMs };
